// Sentiment Analysis and Staff Conduct — moved out of pages/JourneyDetail.jsx
// so the Service Desk can show the same two panels instead of leaving them
// stranded on the page being retired. Both already took their data by a
// `row` prop rather than reading the URL themselves, so — unlike the journey
// stepper's controls — this was a pure relocation, nothing to rewire.
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useSentimentHistory, useStaffToneFlags, useReviewStaffToneFlag } from "../../lib/api";
import { nb } from "../../lib/journey";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { Icon } from "../msd/Icon";
import { Btn, Dot, Tag, CARD, DISP, LBL, cx } from "../msd/ui";

const sentimentColor = (s) =>
  s === "Positive" ? "var(--pine)" : s === "Negative" ? "var(--brick)" : s === "Neutral" ? "var(--amber)" : "var(--line2)";

// .empty — as ui.jsx's Empty, but these carry a test id.
const EMPTY = "m-0 rounded-[14px] border border-dashed border-msd-line2 p-[22px] text-center text-[14px] text-msd-muted";
const LOADING = "flex items-center gap-2 text-[14px] text-msd-muted";
const ERROR = "m-0 text-[13px] text-msd-brick-ink";
const HEADING = "m-0 text-[16.5px] font-bold text-msd-ink";

const humanizeStage = (s) =>
  String(s || "")
    .split("_")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const SENTIMENT_KEYS = ["Positive", "Neutral", "Negative"];

// The conversation starts positive: this many "positive" points are counted
// before any message is analysed. Raise it to make the split move more slowly.
const START_POSITIVE_WEIGHT = 1;

function sentimentMix(rows, startPositive = false) {
  const counts = { Positive: startPositive ? START_POSITIVE_WEIGHT : 0, Neutral: 0, Negative: 0 };
  rows.forEach((r) => {
    if (SENTIMENT_KEYS.includes(r.sentiment)) counts[r.sentiment] += 1;
  });
  const total = SENTIMENT_KEYS.reduce((n, k) => n + counts[k], 0);
  if (!total) return { Positive: 0, Neutral: 0, Negative: 0 };
  // Round down, then give the leftover points to the largest remainders so
  // the three always add up to exactly 100.
  const raw = SENTIMENT_KEYS.map((k) => (counts[k] / total) * 100);
  const result = raw.map(Math.floor);
  const left = 100 - result.reduce((a, b) => a + b, 0);
  raw
    .map((v, i) => [v - result[i], i])
    .sort((a, b) => b[0] - a[0])
    .slice(0, left)
    .forEach(([, i]) => {
      result[i] += 1;
    });
  return { Positive: result[0], Neutral: result[1], Negative: result[2] };
}

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      data-testid="sentiment-info-tooltip"
    >
      <span className="flex cursor-help text-msd-faint">
        <Icon name="info" size={16} stroke={2} />
      </span>
      {show && (
        <div className="absolute left-1/2 top-full z-10 mt-[6px] w-56 -translate-x-1/2 rounded-xl bg-msd-surface px-3 py-2 text-[12.5px] font-normal text-msd-ink2 shadow-msd-pop">
          {text}
        </div>
      )}
    </span>
  );
}

function SentimentBreakdown({ trend }) {
  const current = sentimentMix(trend, true);
  let delta = null;
  if (trend.length >= 2) {
    const mid = Math.ceil(trend.length / 2);
    const earlier = sentimentMix(trend.slice(0, mid));
    const recent = sentimentMix(trend.slice(mid));
    delta = {
      Positive: recent.Positive - earlier.Positive,
      Neutral: recent.Neutral - earlier.Neutral,
      Negative: recent.Negative - earlier.Negative,
    };
  }

  return (
    <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-3" data-testid="sentiment-breakdown">
      {SENTIMENT_KEYS.map((key) => (
        <div key={key}>
          <div className="text-[14px] text-msd-muted">{key}</div>
          <div className="flex flex-wrap items-baseline gap-x-[6px]">
            <span className={cx(DISP, "text-[28px] font-semibold text-msd-ink")}>
              {current[key]}
              <span className="text-[16px]">%</span>
            </span>
            {/* {delta && delta[key] !== 0 && (
              <span className="flex items-center text-[12px] font-medium text-msd-muted">
                <Icon name="arrowup" size={12} stroke={2.2} className={delta[key] > 0 ? "" : "rotate-180"} />
                {Math.abs(delta[key])}%
              </span>
            )} */}
          </div>
          <div className="mt-[6px] h-[6px] overflow-hidden rounded-[3px] bg-msd-line">
            <div className="h-full" style={{ width: `${current[key]}%`, background: sentimentColor(key) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// `embedded`: rendered inside another container (Service Desk details), so no
// card chrome or heading of its own.
export function SentimentHistoryPanel({ row, embedded = false }) {
  const { data, isLoading, error } = useSentimentHistory(row);
  const history = data?.rows || [];
  const escalations = history.filter((h) => h.escalation_flag).length;
  const trend = [...history].reverse();

  return (
    <div className={embedded ? "" : cx(CARD, "p-[22px] text-msd-ink")} data-testid="sentiment-history-panel">
      <div className={embedded ? "hidden" : "mb-4 flex items-center justify-between"}>
        <div className="flex items-center gap-[6px]">
          <h3 className={HEADING}>Sentiment Analysis</h3>
          <InfoTooltip text="Share of messages by sentiment across the conversation. Arrows compare the recent half of the conversation to the earlier half." />
        </div>
        {escalations > 0 && (
          <Tag tone="red" data-testid="escalation-count">
            {escalations} escalated
          </Tag>
        )}
      </div>

      {isLoading ? (
        <div className={LOADING}>
          <Loader2 className="animate-spin" size={16} /> Loading sentiment history…
        </div>
      ) : error ? (
        <p className={ERROR}>Failed to load sentiment history.</p>
      ) : !history.length ? (
        <>
          <SentimentBreakdown trend={[]} />
          <p className={cx(EMPTY, "mt-4")} data-testid="sentiment-history-empty">
            No sentiment signals yet — this fills in once the customer sends a message in the group.
          </p>
        </>
      ) : (
        <>
          <SentimentBreakdown trend={trend} />

          <div className="mb-1 mt-5 font-semibold">Conversation events</div>
          <div className="max-h-[380px] overflow-y-auto" data-testid="sentiment-history-list">
            {history.map((h) => (
              <div key={h.id} className="border-b border-msd-line py-3 last:border-b-0" data-testid={`sentiment-entry-${h.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-[14px]">
                  <Dot color={sentimentColor(h.sentiment)} />
                  <span className="text-msd-ink2">{humanizeStage(h.stage)}</span>
                  {nb(h.decision) && <Tag small>{h.decision}</Tag>}
                  {h.escalation_flag && (
                    <Tag small tone="red">
                      Escalated
                    </Tag>
                  )}
                  <span className={cx(LBL, "ml-auto shrink-0 tabular-nums")}>
                    {new Date(h.created_at).toLocaleString(undefined, {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                {nb(h.triggering_message) && (
                  <div className="mt-[6px] line-clamp-2 text-[16px]" title={h.triggering_message}>
                    "{h.triggering_message}"
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const RISK_TONE = { medium: "orange", high: "red" };

export function StaffToneReviewPanel({ row, compact = false, embedded = false }) {
  const { data, isLoading, error } = useStaffToneFlags(row);
  const review = useReviewStaffToneFlag(row);
  const { roleShort } = useTenantTerms();

  const allFlags = data?.rows || [];
  // Low is kept on file for the record but is not what a supervisor needs to
  // act on — only Medium/High are shown here, matching the spec's own
  // wording ("inappropriate, unprofessional, or high-risk shall be flagged").
  const flags = allFlags.filter((f) => f.risk_level !== "low");
  const openCount = flags.filter((f) => f.status === "open").length;

  return (
    <div
      className={embedded ? "" : cx(CARD, "text-msd-ink", compact ? "p-[14px]" : "p-[22px]")}
      data-testid="staff-tone-panel"
    >
      {/* The badge sits in the corner beside the heading, the same pattern
          Group Members uses for its "4 Total" pill — but the wording has to
          earn its keep at that width. "2 needs review" doesn't: at 320px the
          heading wrapped to "Staff" / "Conduct" and the pill wrapped its own
          text in two, because a full sentence and a heading were fighting for
          one row. "2 review" is short enough to sit beside "Staff Conduct"
          without either one breaking, now that the rail is wider too. */}
      <div className={embedded ? "hidden" : cx("flex items-center justify-between", compact ? "mb-3" : "mb-4")}>
        <div className="flex min-w-0 items-center gap-[6px]">
          <span className="flex shrink-0 text-msd-faint">
            <Icon name="shield" size={17} stroke={2} />
          </span>
          <h3 className={cx(HEADING, compact && "whitespace-nowrap !text-[15px]")}>Staff Conduct</h3>
          <InfoTooltip text="Tone and professionalism of staff messages in this group's chat, judged independently of the customer's sentiment. Low-risk messages are kept on file but not shown here." />
        </div>
        {openCount > 0 && (
          <Tag tone="purple" small={compact} className="shrink-0" data-testid="staff-tone-open-count">
            {openCount} {compact ? "review" : `need${openCount === 1 ? "s" : ""} review`}
          </Tag>
        )}
      </div>

      {isLoading ? (
        <div className={LOADING}>
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : error ? (
        <p className={ERROR}>Failed to load staff conduct flags.</p>
      ) : !flags.length ? (
        <p className={EMPTY} data-testid="staff-tone-empty">
          No tone concerns flagged for this conversation.
        </p>
      ) : (
        // Padded so the scroll area does not clip the boxes' shadows.
        <div className="-m-[3px] flex max-h-[380px] flex-col gap-[10px] overflow-y-auto p-[3px]" data-testid="staff-tone-list">
          {flags.map((f) => (
            <div
              key={f.id}
              className="flex shrink-0 flex-col gap-[10px] rounded-[14px] bg-msd-surface p-[14px] shadow-msd-e1"
              data-testid={`staff-tone-entry-${f.id}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <b>{roleShort(f.sender_role)}</b>
                <span className={cx(LBL, "tabular-nums")}>
                  {new Date(f.created_at).toLocaleString(undefined, {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="ml-auto flex items-center gap-[6px]">
                  <Tag small tone={RISK_TONE[f.risk_level]} className="capitalize">
                    {f.risk_level}
                  </Tag>
                  {f.status === "reviewed" && (
                    <Tag small tone="green">
                      Reviewed
                    </Tag>
                  )}
                </span>
              </div>
              {nb(f.triggering_message) && (
                <div className="line-clamp-2 text-[15.5px]" title={f.triggering_message}>
                  "{f.triggering_message}"
                </div>
              )}
              {nb(f.reason) && <div className={LBL}>{f.reason}</div>}
              {f.status === "open" && (
                <Btn size="sm" className="self-start" onClick={() => review.mutate(f.id)} disabled={review.isPending}>
                  Mark reviewed
                </Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
